import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { formatMoney } from "../../components/cart-redesign/pricing";
import ProfileLayout from "../../components/profile-redesign/ProfileLayout";
import Panel from "../../components/profile-redesign/Panel";
import StatTiles from "../../components/profile-redesign/StatTiles";
import OrdersTable from "../../components/profile-redesign/OrdersTable";
import StateMessage from "../../components/profile-redesign/StateMessage";
import Notice from "../../components/profile-redesign/Notice";
import {
  useProfile,
  useProfileOrders,
} from "../../components/profile-redesign/useProfile";
import {
  ORDER_TABS,
  isActive,
  orderRef,
} from "../../components/profile-redesign/profileData";

function ProfileOrders() {
  const { user, isLoading, isError, signOut } = useProfile();
  const { orders, orderCount, totalSpent, isFetching, isEmpty, isError: ordersFailed } =
    useProfileOrders(user?._id);

  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");

  const currency = useSelector((state) => state.currency);
  const formatPrice = (value) => formatMoney(currency, value);

  const counts = useMemo(
    () =>
      ORDER_TABS.reduce((acc, entry) => {
        acc[entry.key] = orders.filter(entry.match).length;
        return acc;
      }, {}),
    [orders]
  );

  /* Search runs over the reference the table shows, so what you type is
   * what you see. */
  const visible = useMemo(() => {
    const matcher = ORDER_TABS.find((entry) => entry.key === tab)?.match;
    const term = search.trim().toLowerCase();

    return orders
      .filter((order) => (matcher ? matcher(order) : true))
      .filter((order) =>
        term ? orderRef(order._id).toLowerCase().includes(term) : true
      );
  }, [orders, tab, search]);

  const activeCount = orders.filter((order) => isActive(order.status)).length;

  const tiles = [
    { label: "Total Orders", value: String(orderCount).padStart(2, "0"), tone: "blue" },
    { label: "Active Orders", value: String(activeCount).padStart(2, "0"), tone: "amber" },
    { label: "Delivered", value: String(counts.delivered || 0).padStart(2, "0"), tone: "green" },
    { label: "Total Spent", value: formatPrice(totalSpent), tone: "pink" },
  ];

  const body = () => {
    if (isFetching) {
      return <StateMessage tone="loading" title="Loading your orders…" />;
    }
    if (ordersFailed) {
      return (
        <StateMessage
          tone="error"
          title="We couldn't load your orders"
          text="Please refresh the page, or try again in a moment."
        />
      );
    }
    if (isEmpty || !orders.length) {
      return (
        <StateMessage
          title="No orders yet"
          text="Orders appear here as soon as they're confirmed. Pending checkouts aren't listed."
        >
          <div className="c4prf__actions">
            <Link
              className="c4prf__btn c4prf__btn--primary"
              to={process.env.PUBLIC_URL + "/shop-grid-standard"}
            >
              Start Shopping
            </Link>
          </div>
        </StateMessage>
      );
    }

    return (
      <>
        <div className="c4prf__tabs" role="tablist" aria-label="Filter orders">
          {ORDER_TABS.map((entry) => (
            <button
              className={`c4prf__tab${tab === entry.key ? " c4prf__tab--on" : ""}`}
              key={entry.key}
              type="button"
              role="tab"
              aria-selected={tab === entry.key}
              onClick={() => setTab(entry.key)}
            >
              {entry.label} ({counts[entry.key] || 0})
            </button>
          ))}
        </div>

        <div className="c4prf__filters">
          <label className="c4prf__sr-only" htmlFor="c4prf-search">
            Search orders
          </label>
          <input
            className="c4prf__input"
            id="c4prf-search"
            type="search"
            placeholder="Search by order reference…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {visible.length ? (
          <OrdersTable orders={visible} formatPrice={formatPrice} withActions />
        ) : (
          <StateMessage
            title="Nothing matches that filter"
            text="Try another tab, or clear the search box."
          />
        )}
      </>
    );
  };

  return (
    <ProfileLayout
      seoTitle="Order History"
      title="Order History"
      subtitle="View, track and manage your past and current orders."
      user={user}
      isLoading={isLoading}
      isError={isError}
      onSignOut={signOut}
    >
      <StatTiles tiles={tiles} />

      <Panel id="c4prf-orders" title="All Orders">
        {body()}
      </Panel>

      <Notice>
        Order references are shortened from each order's ID, and dates come
        from when the order was created — the history endpoint returns the
        items, totals and status only.
      </Notice>
    </ProfileLayout>
  );
}

export default ProfileOrders;
